export const CATALOGUE_PRICE_PRESETS = ["DEFAULT", "EXPORT", "B2B", "RETAIL"] as const;
export const PRICE_SAFETY_PCT = 10;

export type CataloguePriceTier = {
  mode: "preset" | "custom";
  preset: string;
  custom: string;
};

export type PricingSheetEntry = {
  productUniqueId: string;
  name: string;
  productDefaultPrice: number;
  cataloguePrice: number | null;
  effectivePrice: number;
  usesProductDefault: boolean;
  clientTier: string;
};

export type PricingUpsertEntry = {
  productUniqueId: string;
  cataloguePrice: number | null;
};

const pricingAuthHeaders = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export const defaultCataloguePriceTier = (): CataloguePriceTier => ({
  mode: "preset",
  preset: "DEFAULT",
  custom: "",
});

export function effectiveClientTier(tier: CataloguePriceTier): string {
  if (tier.mode === "custom" && tier.custom.trim()) {
    return tier.custom.trim().toUpperCase();
  }
  if (tier.mode === "custom") {
    return "DEFAULT";
  }
  return tier.preset || "DEFAULT";
}

export function parseRowEffective(
  row: PricingSheetEntry,
  inputs: Record<string, string>
): number {
  const raw = (inputs[row.productUniqueId] ?? "").trim();
  if (raw === "") return row.productDefaultPrice;
  const n = parseFloat(raw);
  if (Number.isNaN(n) || n < 0) return row.productDefaultPrice;
  return n;
}

export function changePctVsDefault(oldPrice: number, newPrice: number): number {
  if (oldPrice > 0) return ((newPrice - oldPrice) / oldPrice) * 100;
  if (newPrice > 0) return 100;
  return 0;
}

export function inputsFromSheet(rows: PricingSheetEntry[]): Record<string, string> {
  const inputs: Record<string, string> = {};
  rows.forEach((row) => {
    inputs[row.productUniqueId] =
      row.cataloguePrice != null ? String(row.cataloguePrice) : "";
  });
  return inputs;
}

export function mapPricingSheetEntries(raw: any[], clientTier: string): PricingSheetEntry[] {
  return (raw || []).map((entry: any) => ({
    productUniqueId: String(entry.productUniqueId || ""),
    name: String(entry.name || entry.productUniqueId || "Product"),
    productDefaultPrice: Number(entry.productDefaultPrice) || 0,
    cataloguePrice: entry.cataloguePrice != null ? Number(entry.cataloguePrice) : null,
    effectivePrice: Number(entry.effectivePrice) || 0,
    usesProductDefault: Boolean(entry.usesProductDefault),
    clientTier: entry.clientTier || clientTier,
  }));
}

export async function fetchCataloguePricingSheet(
  catalogueId: string,
  clientTier: string
): Promise<{ entries: PricingSheetEntry[]; clientTier: string }> {
  const tierParam = encodeURIComponent(clientTier);
  const res = await fetch(
    `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogueId)}/pricing?clientTier=${tierParam}`,
    { headers: pricingAuthHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) {
    throw new Error(json?.message || "Could not load catalogue pricing.");
  }
  const tier = String(json.data?.clientTier || clientTier);
  return {
    clientTier: tier,
    entries: mapPricingSheetEntries(json.data?.entries || [], tier),
  };
}

export async function saveCataloguePricing(
  catalogueId: string,
  clientTier: string,
  entries: PricingUpsertEntry[]
): Promise<string> {
  const res = await fetch(
    `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogueId)}/pricing`,
    {
      method: "PUT",
      headers: pricingAuthHeaders(),
      body: JSON.stringify({ clientTier, entries }),
    }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) {
    throw new Error(json?.message || "Could not save pricing.");
  }
  return String(json.data?.clientTier || clientTier);
}

export type CataloguePriceQuoteSummary = {
  id: string;
  clientName: string;
  clientTier?: string;
  shareLinkId?: string;
  createdAt?: string;
  entryCount?: number;
};

export type CataloguePriceQuoteLine = {
  productUniqueId: string;
  productName: string;
  price: number;
};

export type CataloguePriceQuoteDetail = CataloguePriceQuoteSummary & {
  entries: CataloguePriceQuoteLine[];
};

export type CataloguePriceQuoteClientGroup = {
  clientName: string;
  quotes: CataloguePriceQuoteSummary[];
};

export function groupQuotesByClient(
  quotes: CataloguePriceQuoteSummary[]
): CataloguePriceQuoteClientGroup[] {
  const groups = new Map<string, CataloguePriceQuoteSummary[]>();
  const order: string[] = [];
  for (const quote of quotes) {
    const name = String(quote.clientName || "").trim() || "Unnamed";
    const key = name.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(quote);
  }
  return order.map((key) => {
    const grouped = groups.get(key)!;
    return { clientName: grouped[0].clientName || "Unnamed", quotes: grouped };
  });
}

const mapQuoteSummary = (raw: any): CataloguePriceQuoteSummary => ({
  id: String(raw?.id || raw?._id || ""),
  clientName: String(raw?.clientName || ""),
  clientTier: raw?.clientTier ? String(raw.clientTier) : undefined,
  shareLinkId: raw?.shareLinkId ? String(raw.shareLinkId) : undefined,
  createdAt: raw?.createdAt ? String(raw.createdAt) : undefined,
  entryCount: Number(raw?.entryCount) || 0,
});

const quoteApiError = (json: any, fallback: string): string =>
  String(json?.message || json?.error || fallback);

export async function rememberCataloguePrices(
  catalogueId: string,
  body: { clientName: string; clientTier?: string; shareLinkId?: string }
): Promise<CataloguePriceQuoteSummary> {
  const res = await fetch(
    `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogueId)}/price-quotes`,
    {
      method: "POST",
      headers: pricingAuthHeaders(),
      body: JSON.stringify(body),
    }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) {
    throw new Error(quoteApiError(json, "Could not remember catalogue prices."));
  }
  return mapQuoteSummary(json.data);
}

export async function fetchCataloguePriceQuotes(
  catalogueId: string
): Promise<CataloguePriceQuoteSummary[]> {
  const res = await fetch(
    `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogueId)}/price-quotes`,
    { headers: pricingAuthHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) {
    throw new Error(quoteApiError(json, "Could not load price diary."));
  }
  return (json.data?.quotes || []).map(mapQuoteSummary).filter((row: CataloguePriceQuoteSummary) => row.id);
}

export async function fetchCataloguePriceQuote(
  catalogueId: string,
  quoteId: string
): Promise<CataloguePriceQuoteDetail> {
  const res = await fetch(
    `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogueId)}/price-quotes/${encodeURIComponent(quoteId)}`,
    { headers: pricingAuthHeaders() }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json?.success) {
    throw new Error(quoteApiError(json, "Could not load quote."));
  }
  const summary = mapQuoteSummary(json.data);
  const entries = (json.data?.entries || []).map((entry: any) => ({
    productUniqueId: String(entry.productUniqueId || ""),
    productName: String(entry.productName || entry.productUniqueId || "Product"),
    price: Number(entry.price) || 0,
  }));
  return { ...summary, entries };
}

export function buildPricingUpsertEntries(
  rows: PricingSheetEntry[],
  inputs: Record<string, string>
): PricingUpsertEntry[] {
  const payload: PricingUpsertEntry[] = [];
  for (const row of rows) {
    const raw = (inputs[row.productUniqueId] ?? "").trim();
    if (raw === "") {
      payload.push({ productUniqueId: row.productUniqueId, cataloguePrice: null });
      continue;
    }
    const n = parseFloat(raw);
    if (Number.isNaN(n) || n < 0) {
      throw new Error(
        `Invalid price for ${row.name}. Use a non-negative number or leave blank for the product default.`
      );
    }
    payload.push({ productUniqueId: row.productUniqueId, cataloguePrice: n });
  }
  return payload;
}
